import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';
import { Loading, Calendar, QuestionPanel, Button } from '../../components';
import moment from 'moment';
import { CurrencyUtils, TranslationUtils } from '../../utils';
import { useTripStoreUpdater } from '../../hooks';
import { AdjustmentPreviewRequest } from 'guest-data-types-ts-lib';
import { TripService } from '../../services/trip/tripService';
import ddLogger from '../../logging/ddLogger';
import { ExtendedStayPreviewRequest, ExtendStayPreviewRequest, TripAdjustmentResponse } from 'guest-data-types-ts-lib/types/adjustments';
import './ExtendedStay.scss';
import { AppRoutes } from '../../routes';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { AlertModal } from '../../components/AlertModal/AlertModal';

export enum Alert {
    ERROR = 'ERROR',
    WARNING = 'WARNING',
    SUCCESS = 'SUCCESS',
}

const defaultResponse = {
    preview: null,
    isLoading: false,
    error: '',
};

export const ExtendedStay: React.FC = () => {
    useTripStoreUpdater(false, true);
    const history = useHistory();
    const { trip } = useSelector((store: StoreState) => store);
    const [newCheckIn, setCheckIn] = useState<moment.Moment>();
    const [newCheckOut, setCheckOut] = useState<moment.Moment>();
    const [reservationId, setReservationId] = useState<string | null>(null);
    const [clean, setClean] = useState<boolean>(false);
    const [tryPreview, setTryPreview] = useState<boolean>(false);
    const [response, setResponse] = useState<{ preview: TripAdjustmentResponse | null; isLoading: boolean }>(defaultResponse);
    const [alert, setAlert] = useState<Alert | null>(null);
    const errorMessage = TranslationUtils.get('extended_stay_error_message');

    useEffect(() => {
        (async () => {
            if (tryPreview && !_.isNil(newCheckIn) && !_.isNil(newCheckOut) && !_.isNil(reservationId)) {
                setResponse({ ...response, isLoading: true });
                const last_night: string = newCheckOut.clone().subtract(1, 'days').format('YYYY-MM-DD');
                const adjustment: ExtendedStayPreviewRequest = {
                    name: 'extendedStay',
                    data: {
                        first_night: newCheckIn.format('YYYY-MM-DD'),
                        last_night,
                    },
                };
                await adjustmentPreview(reservationId, adjustment);
            }
        })();
    }, [newCheckIn, newCheckOut]);

    if (trip.isLoading) {
        return (
            <div className={'fix-loading'}>
                <Loading />
            </div>
        );
    }

    const { reservation, stay } = trip;

    if (!reservation || !stay) {
        return null;
    }

    const previewIsNeeded =
        !_.isEqual(newCheckIn?.format('YYYY-MM-DD'), stay.check_in_date) ||
        !_.isEqual(newCheckOut?.format('YYYY-MM-DD'), stay.check_out_date);

    const adjustmentPreview = async (reservationId: string, adjustment: AdjustmentPreviewRequest) => {
        setResponse({ ...response, isLoading: true });
        try {
            ddLogger.info(`previewing adjustment ${adjustment.name} for ${reservationId}`);
            const tripService = new TripService();
            const data = await tripService.getAdjustmentPreview(reservationId, adjustment);
            const preview = data.attributes as TripAdjustmentResponse;

            if (preview.delta.taxes < 0 || preview.delta.fees < 0 || preview.delta.rent < 0) {
                setResponse({
                    ...response,
                    isLoading: false,
                });
                setAlert(Alert.ERROR);
                ddLogger.warn(`WARNING adjustment ${adjustment.name} previewed with negative values`, {
                    preview,
                });
                return;
            }

            setResponse({ ...response, isLoading: false, preview });
            setAlert(Alert.SUCCESS);
            ddLogger.info(`adjustment ${adjustment.name} successfully previewed`, {
                preview,
            });
        } catch (e) {
            setResponse({ ...response, isLoading: false });
            setAlert(Alert.ERROR);
            const code = e.code;
            ddLogger.error(`ERROR previewing ${adjustment.name} adjustment for ${reservationId}`, {
                adjustment: adjustment.name,
                error: e.message,
                code,
            });
        }
    };

    const clearDates = () => {
        const checkIn = moment(stay.check_in_date);
        const checkOut = moment(stay.check_out_date);
        setTryPreview(false);
        setCheckIn(checkIn);
        setCheckOut(checkOut);
        setClean(!clean);
        setResponse(defaultResponse);
        setAlert(null);
    };

    const onClick = () => {
        if (!_.isNil(response.preview) && !_.isNil(newCheckIn) && !_.isNil(newCheckOut)) {
            const { adjustment, delta, original_finances } = response.preview;

            ddLogger.info(`adjustment ${adjustment.name} successfully previewed`, {
                delta: delta,
                original_total: original_finances?.total,
            });

            history.push({
                pathname: AppRoutes.MODIFY_TRIP_ADJUSTMENTS.replace(':adjustment', 'extended_stay'),
                state: { preview: response.preview },
            });
        }
    };

    return (
        <div id={'main-extended-stay'} className={'main-extended-stay-content'}>
            <h1 tabIndex={0} className={'page-title font-extended-stay-title'}>
                {TranslationUtils.get('extended_stay_capitalized')}
            </h1>
            <p id={'extended-stay-rules'} className={'body-small extended-stay-rules-content'}>
                {TranslationUtils.get('extended_stay_rules')}
            </p>
            <Calendar
                setCheckIn={(date: moment.Moment) => {
                    setTryPreview(true);
                    setCheckIn(date);
                }}
                setCheckOut={(date: moment.Moment) => {
                    setTryPreview(true);
                    setCheckOut(date);
                }}
                checkIn={stay.check_in_date}
                checkOut={stay.check_out_date}
                reservationId={reservation.id}
                setReservationId={setReservationId}
                clean={clean}
                isLoading={response.isLoading}
                setError={(value) => setAlert(value)}
            />
            <div id={'empty-div'} className={'empty-division-modals'} />
            <div id={'extended-stay-alert'} className={'d-flex flex-column justify-content-end alert-content'}>
                {_.isNil(alert) && <div className={'empty-division-alerts'} />}
                {(alert === Alert.ERROR || alert === Alert.WARNING) && (
                    <AlertModal
                        variant={alert === Alert.ERROR ? 'danger' : 'warning'}
                        label={errorMessage}
                        onClick={() => setAlert(null)}
                    />
                )}
                {!_.isNil(response.preview) && alert === Alert.SUCCESS && (
                    <AlertModal
                        variant={'success'}
                        label={
                            (response.preview.adjustment as ExtendStayPreviewRequest).data.additional_nights_count > 1
                                ? TranslationUtils.get('extended_stay_success_alert_plural', {
                                      numberNights: (response.preview.adjustment as ExtendStayPreviewRequest).data.additional_nights_count,
                                      price: CurrencyUtils.formatCurrency(
                                          _.toNumber(response.preview.delta.total),
                                          response.preview.delta.currency
                                      ),
                                  })
                                : TranslationUtils.get('extended_stay_success_alert', {
                                      price: CurrencyUtils.formatCurrency(
                                          _.toNumber(response.preview.delta.total),
                                          response.preview.delta.currency
                                      ),
                                  })
                        }
                        onClick={() => setAlert(null)}
                    />
                )}
            </div>
            <div id={'extended-stay-btn'} className={'extended-stay-btn-content'}>
                <Button
                    id={'btn-submit'}
                    className={'btn-preview heading-xs'}
                    label={TranslationUtils.get('checkout')}
                    variant={'primary'}
                    onClick={() => onClick()}
                    disabled={_.isNil(response.preview)}
                    isLoading={response.isLoading}
                />
                <Button
                    id={'btn-reset-dates'}
                    className={'font-btn-calendar font-underline'}
                    label={TranslationUtils.get('reset_dates')}
                    variant={'default'}
                    onClick={() => clearDates()}
                    disabled={response.isLoading || !previewIsNeeded}
                />
            </div>
            <QuestionPanel />
        </div>
    );
};
